import { useContext } from 'react';
import { Helmet } from 'react-helmet-async';
import { Navigate, useParams } from 'react-router';

import { Heading1 } from '@components/commons';
import GalleryLayout from '@components/Gallery/GalleryLayout';
import { Photo } from '@types';
import { capitalize } from '@utils/formatters';
import { getStartEndDates, StartEndDates } from '@utils/getters';
import { sortSeriesByDates } from '@utils/sort';
import { DataContext } from '@utils/useData';
import Location from './Location';
import { getZoneSeries } from './utils';

const Locations = () => {
  const { zone } = useParams();
  if (!zone) return <Navigate to="/" />;

  const { photos, series } = useContext(DataContext);
  const displayedLocations = series.filter(
    ({ location }) => !!location && location.city !== 'Paris',
  );
  const zoneLocations = getZoneSeries(displayedLocations, zone);

  const photosByLocation = photos.reduce<Record<string, Photo[]>>(
    (data, photo) => {
      photo.seriesIds?.forEach((seriesId) => {
        if (displayedLocations.find(({ id }) => seriesId === id)) {
          if (data[seriesId]) data[seriesId].push(photo);
          else data[seriesId] = [photo];
        }
      });
      return data;
    },
    {},
  );
  const datesByLocations = Object.keys(photosByLocation).reduce<
    Record<string, StartEndDates>
  >((data, id) => {
    const locationPhotos = photosByLocation[id];
    return { ...data, [id]: getStartEndDates(locationPhotos) };
  }, {});

  const highlights = zoneLocations.highlights[zone] || [];
  const other = zoneLocations.other[zone] || [];

  return (
    <div className="desktop:py-12">
      <Helmet>
        <title>Marc Malignan | {capitalize(zone)}</title>
      </Helmet>
      <Heading1 className="desktop:hidden mb-10 text-center">{zone}</Heading1>
      <div className="desktop:flex desktop:gap-20">
        <section className="grow basis-0">
          <GalleryLayout
            className="mb-8"
            gap="gap-x-4 gap-y-8"
            nbPerRowDesktop={2}
            nbPerRowTablet={3}
            nbPerRowPhone={2}
            elements={highlights.sort(
              sortSeriesByDates(datesByLocations, photosByLocation),
            )}
            renderElement={(location) => (
              <Location
                location={location}
                photosByLocation={photosByLocation}
                zone={zone}
              />
            )}
          />
        </section>
        <section className="grow basis-0 max-desktop:mt-8">
          <GalleryLayout
            className="mb-8 desktop:justify-start"
            gap="gap-x-4 gap-y-8"
            nbPerRowDesktop={3}
            nbPerRowTablet={4}
            nbPerRowPhone={2}
            elements={other.sort(
              sortSeriesByDates(datesByLocations, photosByLocation),
            )}
            renderElement={(location) => (
              <Location
                location={location}
                photosByLocation={photosByLocation}
                zone={zone}
              />
            )}
          />
        </section>
      </div>
    </div>
  );
};

export default Locations;
